import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from 'convex/react'
import { Globe } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/auth-context'
import { api } from '@/../convex/_generated/api'
import { type Directory } from '@/routes/admin/directories/data'
import { SITE_HOST, cdnUrl } from '@/lib/config/site'

type QueueFilter = 'queued' | 'paid' | 'free' | 'live'

/** sessionStorage keys for preserving the queue's UI state across detail-page round trips. */
const SCROLL_KEY = 'directory-queue:scrollY'
const FILTER_KEY = 'directory-queue:filter'

/** AppLayout wraps the page in <main className="flex-1 overflow-auto">, so vertical scroll lives there, not on window. There can be more than one <main> in the tree (mobile/desktop variants), so target the one that's actually scrollable. */
function getScrollContainer(): HTMLElement | null {
  if (typeof document === 'undefined') return null
  const mains = document.querySelectorAll<HTMLElement>('main')
  for (const el of Array.from(mains)) {
    if (el.scrollHeight > el.clientHeight) return el
  }
  // Fallback: any <main> with overflow:auto in its computed style.
  for (const el of Array.from(mains)) {
    const style = window.getComputedStyle(el)
    if (style.overflowY === 'auto' || style.overflowY === 'scroll') return el
  }
  return mains[0] ?? null
}

/** Bunny path for any captured submit-page thumbnails. Same convention the admin detail page uses. */
const SCREENSHOT_BASE = cdnUrl('/directory-screenshots')

const PRICING_LABEL: Record<string, string> = {
  free: 'Free',
  freemium: 'Freemium',
  paid: 'Paid',
  'editorial-pitch': 'Editorial pitch',
  'auto-scrape': 'Auto-listed',
}

function drTone(dr: number): string {
  if (dr >= 80) return 'text-emerald-700 bg-emerald-50 ring-emerald-600/20'
  if (dr >= 60) return 'text-emerald-600 bg-emerald-50 ring-emerald-600/15'
  if (dr >= 40) return 'text-amber-700 bg-amber-50 ring-amber-600/20'
  return 'text-neutral-600 bg-neutral-50 ring-neutral-300/40'
}

function bestThumb(d: Directory): string | null {
  if (d.screenshotUrl) return d.screenshotUrl
  return `${SCREENSHOT_BASE}/${d.slug}.jpg`
}

/** Minimum DR worth submitting to — anything below this is too low-authority to bother. */
const MIN_DR = 30

/** Filters out junk flags (defunct/duplicate/non-dir/low-quality/ineligible/review), eligibility=false, and anything below MIN_DR. Does NOT require dofollow=Yes — that gate is applied per-filter below. */
function isActionable(d: Directory): boolean {
  if (d.flag === 'defunct' || d.flag === 'non-directory' || d.flag === 'duplicate' || d.flag === 'low-quality' || d.flag === 'ineligible' || d.flag === 'review') return false
  if (d.eligibility && d.eligibility.eligible === false) return false
  if ((d.domainRating ?? 0) < MIN_DR) return false
  return true
}

export default function AdminDirectoryQueuePage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin
  const location = useLocation()
  const directoriesQuery = useQuery(api.directories.list, isAdmin ? {} : 'skip')
  // Initial filter comes from sessionStorage so a back-nav from a detail page
  // doesn't bounce the user back to the default "queued" tab.
  const [filter, setFilter] = useState<QueueFilter>(() => {
    if (typeof window === 'undefined') return 'queued'
    const saved = window.sessionStorage.getItem(FILTER_KEY)
    return saved === 'paid' || saved === 'free' || saved === 'live' || saved === 'queued' ? saved : 'queued'
  })
  // Persist filter changes so back-nav restores them.
  useEffect(() => {
    if (typeof window === 'undefined') return
    window.sessionStorage.setItem(FILTER_KEY, filter)
  }, [filter])

  // Restore scroll once the data is in and the list has rendered.
  const dataReady = directoriesQuery !== undefined
  const restoredFor = useRef<string | null>(null)
  useEffect(() => {
    if (!dataReady) return
    if (restoredFor.current === location.key) return
    restoredFor.current = location.key
    if (typeof window === 'undefined') return
    const saved = window.sessionStorage.getItem(SCROLL_KEY)
    if (!saved) return
    const y = parseInt(saved, 10)
    if (Number.isNaN(y) || y <= 0) return
    // Two RAFs so layout has flushed (data → DOM → measure) before we scroll.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = getScrollContainer()
        if (el) el.scrollTop = y
        else window.scrollTo(0, y)
      })
    })
  }, [dataReady, location.key])

  // Save scroll continuously so we have an accurate value even on browser back / unmount.
  useEffect(() => {
    const el = getScrollContainer()
    if (!el) return
    let rafId = 0
    const onScroll = () => {
      if (rafId) return
      rafId = requestAnimationFrame(() => {
        rafId = 0
        window.sessionStorage.setItem(SCROLL_KEY, String(el.scrollTop))
      })
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      el.removeEventListener('scroll', onScroll)
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [])

  const pool = useMemo<Directory[]>(() => {
    const list = (directoriesQuery ?? []) as Directory[]
    return list.filter(isActionable).sort((a, b) => (b.domainRating || 0) - (a.domainRating || 0))
  }, [directoriesQuery])

  // queued/paid/free are the "future submissions" view → require confirmed dofollow.
  // live is "what's already in the wild" → any accepted listing, dofollow or not.
  const counts = useMemo(() => {
    const queued = pool.filter(d => d.status !== 'accepted' && d.dofollow === 'Yes')
    return {
      queued: queued.length,
      paid: queued.filter(d => d.cost && d.cost > 0).length,
      free: queued.filter(d => !d.cost || d.cost === 0).length,
      live: pool.filter(d => d.status === 'accepted').length,
    }
  }, [pool])

  const rows = useMemo<Directory[]>(() => {
    if (filter === 'live') return pool.filter(d => d.status === 'accepted')
    const queued = pool.filter(d => d.status !== 'accepted' && d.dofollow === 'Yes')
    if (filter === 'paid') return queued.filter(d => d.cost && d.cost > 0)
    if (filter === 'free') return queued.filter(d => !d.cost || d.cost === 0)
    return queued
  }, [pool, filter])

  if (!isAdmin) {
    return (
      <div className="p-8 text-sm text-neutral-500">Admin only.</div>
    )
  }

  return (
    <div className="min-h-screen bg-[#fafaf7]" style={{ fontFamily: '"Crimson Text", Georgia, serif' }}>
      <div className="mx-auto max-w-3xl px-4 pb-24 pt-8 sm:pt-12">
        <header className="mb-8 sm:mb-10">
          <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-500" style={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace' }}>
            Backlink queue
          </p>
          <h1 className="mt-2 text-[32px] leading-[1.05] tracking-tight text-neutral-900 sm:text-[44px]" style={{ fontFamily: '"DM Serif Display", Georgia, serif' }}>
            Directories to submit to
          </h1>
          <p className="mt-3 max-w-prose text-sm leading-relaxed text-neutral-600 sm:text-base">
            {filter === 'live'
              ? `Listings already live on ${SITE_HOST} — dofollow and nofollow combined.`
              : 'Confirmed dofollow only. Tap a row to open the submit page.'}
          </p>
          <div
            role="tablist"
            aria-label="Filter directories"
            className="mt-5 flex flex-wrap items-baseline gap-x-5 gap-y-3 text-xs"
            style={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace' }}
          >
            {([
              { key: 'queued', label: 'queued', count: counts.queued },
              { key: 'paid', label: 'paid', count: counts.paid },
              { key: 'free', label: 'free', count: counts.free },
              { key: 'live', label: 'live', count: counts.live },
            ] as const).map(({ key, label, count }) => {
              const active = filter === key
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setFilter(key)}
                  className={cn(
                    'group inline-flex items-baseline gap-1.5 rounded-md px-1.5 py-1 -mx-1.5 -my-1 transition',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900/20',
                    active
                      ? 'text-neutral-900'
                      : 'text-neutral-500 hover:text-neutral-900'
                  )}
                >
                  <span
                    className={cn(
                      'text-lg font-medium tabular-nums tracking-tight transition',
                      active ? 'text-neutral-900' : 'text-neutral-400 group-hover:text-neutral-900'
                    )}
                  >
                    {count}
                  </span>
                  <span className={cn('transition', active && 'border-b border-neutral-900')}>{label}</span>
                </button>
              )
            })}
          </div>
        </header>

        <ul className="space-y-2.5">
          {rows.map((d) => {
            const thumb = bestThumb(d)
            const fee = d.cost && d.cost > 0
              ? `$${d.cost}${d.costCadence === 'monthly' ? '/mo' : d.costCadence === 'yearly' ? '/yr' : ''}`
              : 'Free'
            return (
              <li key={d.slug}>
                <Link
                  to={`/admin/directories/${d.slug}`}
                  onClick={() => {
                    // Stash scroll position so the back-nav effect can restore it.
                    const el = getScrollContainer()
                    if (el) window.sessionStorage.setItem(SCROLL_KEY, String(el.scrollTop))
                  }}
                  className="group relative flex items-stretch gap-3 overflow-hidden rounded-2xl border border-neutral-200/70 bg-white shadow-[0_1px_0_rgba(0,0,0,0.02)] transition hover:border-neutral-300 hover:shadow-[0_4px_24px_-12px_rgba(0,0,0,0.12)] focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900/20 sm:gap-4"
                  aria-label={`Open ${d.name} details`}
                >
                  <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-l-2xl bg-neutral-100 sm:h-28 sm:w-36">
                    {thumb ? (
                      <img
                        src={thumb}
                        alt=""
                        className="h-full w-full object-cover object-top"
                        loading="lazy"
                        onError={(e) => {
                          const img = e.currentTarget
                          img.style.display = 'none'
                          const sibling = img.nextElementSibling as HTMLElement | null
                          if (sibling) sibling.style.display = 'flex'
                        }}
                      />
                    ) : null}
                    <div
                      className="absolute inset-0 hidden items-center justify-center bg-gradient-to-br from-neutral-100 to-neutral-200 text-neutral-400"
                      style={{ display: thumb ? 'none' : 'flex' }}
                    >
                      <Globe className="h-5 w-5" />
                    </div>
                  </div>

                  <div className="flex min-w-0 flex-1 flex-col justify-center py-3 pr-4 sm:py-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h2 className="truncate text-[18px] font-normal leading-tight text-neutral-900 transition group-hover:text-neutral-700 sm:text-[20px]" style={{ fontFamily: '"DM Serif Display", Georgia, serif' }}>
                          {d.name}
                        </h2>
                        <p className="mt-0.5 truncate text-[11px] text-neutral-500" style={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace' }}>
                          {d.target}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ring-1 ring-inset ${drTone(d.domainRating || 0)}`}
                        style={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace' }}
                        title="Domain Rating"
                      >
                        DR {d.domainRating ?? '—'}
                      </span>
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-neutral-500" style={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace' }}>
                      <span className={d.cost && d.cost > 0 ? 'text-amber-700' : 'text-emerald-700'}>{fee}</span>
                      {d.pricingModel && d.pricingModel !== 'free' && d.pricingModel !== 'paid' && (
                        <span>{PRICING_LABEL[d.pricingModel] ?? d.pricingModel}</span>
                      )}
                      {filter === 'live' && (
                        <span
                          className={cn(
                            d.dofollow === 'Yes' && 'text-sky-700',
                            d.dofollow === 'Nofollow' && 'text-neutral-400',
                            (d.dofollow === 'None' || !d.dofollow) && 'text-neutral-400'
                          )}
                        >
                          {d.dofollow === 'Yes' ? 'Dofollow' : d.dofollow === 'Nofollow' ? 'Nofollow' : d.dofollow || '—'}
                        </span>
                      )}
                      {d.submitStatus === 'login-wall' && <span className="text-rose-600">Login required</span>}
                      {d.submitStatus === 'broken' && <span className="text-rose-600">Broken</span>}
                      {filter !== 'live' && !d.submissionUrl && <span className="text-neutral-400">No submit URL captured</span>}
                    </div>
                  </div>
                </Link>
              </li>
            )
          })}
          {rows.length === 0 && directoriesQuery !== undefined && (
            <li className="rounded-2xl border border-dashed border-neutral-300 bg-white p-8 text-center text-sm text-neutral-500">
              {filter === 'live'
                ? 'No live listings yet. Mark a directory as accepted on its detail page once the listing goes live.'
                : filter === 'paid'
                  ? 'No paid directories in the queue.'
                  : filter === 'free'
                    ? 'No free directories in the queue.'
                    : "No directories in the queue. The dofollow audit hasn't surfaced anything yet."}
            </li>
          )}
          {directoriesQuery === undefined && (
            <li className="rounded-2xl border border-neutral-200 bg-white p-8 text-center text-sm text-neutral-400">
              Loading the queue…
            </li>
          )}
        </ul>
      </div>
    </div>
  )
}
